import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Loading from "../components/Loading";
import { useGetMatkaMarketsQuery, useCancelMatkaEventMutation } from "../services/matka/matkaApi";

const PAGE_SIZE = 20;

const to12Hour = (time) => {
  if (!time) return '-';
  const [h, m] = time.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return time;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${period}`;
};

const statusBadge = (game) => {
  const map = {
    open:            { cls: "bg-green-100 text-green-700",  label: "Open" },
    closed:          { cls: "bg-red-100 text-red-700",      label: "Closed" },
    result_declared: { cls: "bg-blue-100 text-blue-700",    label: "Result Declared" },
  };
  const s = map[game.status] || { cls: "bg-gray-100 text-gray-600", label: game.status || "-" };
  return (
    <span className={`px-2 py-0.5 text-xs rounded font-medium ${s.cls}`}>
      {s.label}
    </span>
  );
};

const InplayMatkaPage = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, game: null });

  const isAdmin = (() => {
    try {
      const userData = localStorage.getItem('user_data');
      if (userData) return JSON.parse(userData).userType === 'Admin';
    } catch { return false; }
    return false;
  })();
  const [cancelMatkaEvent, { isLoading: isCancelling }] = useCancelMatkaEventMutation();

  const handleCancelEvent = async () => {
    if (!confirmModal.game) return;
    const { eventId } = confirmModal.game;
    const market = eventId?.split('-').slice(3).join('-') || '';
    try {
      await cancelMatkaEvent({ market, eventId }).unwrap();
      refetch();
    } catch (err) {
      alert(err?.data?.message || 'Failed to cancel event');
    } finally {
      setConfirmModal({ isOpen: false, game: null });
    }
  };

  const { data, isLoading, error, refetch } = useGetMatkaMarketsQuery({ filter: 'inplay' });

  const games = data?.data || [];
  const totalPages = Math.ceil(games.length / PAGE_SIZE);
  const paginated = games.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const summary = {
    totalMarkets: games.length,
    totalBets:    games.reduce((s, g) => s + (g.totalBets  || 0), 0),
    totalStake:   games.reduce((s, g) => s + (g.totalStake || 0), 0),
    pendingBets:  games.reduce((s, g) => s + (g.pendingBets || 0), 0),
  };

  return (
    <>
    <div className="w-full">
      <div className="bg-[#0F2327] px-5 py-3 flex items-center justify-between">
        <span className="text-white text-xl font-semibold">Active Matka Games</span>
        <div className="flex gap-2">
          <button onClick={refetch} className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 text-sm transition-colors">
            Refresh
          </button>
          <button onClick={() => navigate(-1)} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 text-sm transition-colors">
            Back
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {!isLoading && (
        <div className="flex flex-wrap gap-3 px-5 py-4 bg-gray-50 border-b border-gray-200">
          {[
            { label: 'Total Markets', value: summary.totalMarkets, cls: 'text-gray-800' },
            { label: 'Total Bets',    value: summary.totalBets,    cls: 'text-blue-600' },
            { label: 'Total Stake',   value: summary.totalStake,   cls: 'text-indigo-600' },
            { label: 'Pending Bets',  value: summary.pendingBets,  cls: summary.pendingBets > 0 ? 'text-orange-500' : 'text-gray-400' },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-gray-200 rounded px-5 py-3 min-w-[140px]">
              <div className="text-xs text-gray-500 mb-1">{s.label}</div>
              <div className={`text-xl font-semibold ${s.cls}`}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="mx-4 my-3 p-3 bg-red-100 text-red-700 border border-red-300 text-sm">
          {error?.data?.message || "Failed to load matka games"}
        </div>
      )}

      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center py-16"><Loading /></div>
        ) : (
          <>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-[#0F2327] text-white">
                  <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">#</th>
                  <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">Name</th>
                  <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">Date</th>
                  <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">Status</th>
                  <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">Bet Close</th>
                  <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">Result Time</th>
                  <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">Time Left</th>
                  <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">Total Bets</th>
                  {/* <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">Total Exposure</th> */}
                  {/* <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">Pending</th> */}
                  {/* <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">Last Result</th> */}
                  <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">Action</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="text-center py-12 text-gray-400 border border-gray-200">
                      No active matka games found
                    </td>
                  </tr>
                ) : (
                  paginated.map((game, idx) => {
                    return (
                      <tr key={game.eventId} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                        <td className="px-4 py-2.5 border border-gray-200">{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                        <td className="px-4 py-2.5 border border-gray-200 font-medium capitalize text-gray-800">{game.name}</td>
                        <td className="px-4 py-2.5 border border-gray-200 text-xs text-gray-600">{game.eventId?.split('-').slice(0, 3).join('-') || '-'}</td>
                        <td className="px-4 py-2.5 border border-gray-200">{statusBadge(game)}</td>
                        <td className="px-4 py-2.5 border border-gray-200">{to12Hour(game.betCloseTime)}</td>
                        <td className="px-4 py-2.5 border border-gray-200">{to12Hour(game.resultTime)}</td>
                        <td className="px-4 py-2.5 border border-gray-200 text-blue-600 font-medium">{game.timeRemainingLabel || '-'}</td>
                        <td className="px-4 py-2.5 border border-gray-200">{game.totalBets ?? 0}</td>
                        {/* <td className="px-4 py-2.5 border border-gray-200">{game.totalStake ?? 0}</td> */}
                        {/* <td className="px-4 py-2.5 border border-gray-200">
                          {game.pendingBets > 0
                            ? <span className="text-orange-600 font-medium">{game.pendingBets}</span>
                            : <span className="text-gray-400">0</span>
                          }
                        </td> */}
                        {/* <td className="px-4 py-2.5 border border-gray-200 text-xs">
                          {hasResult ? (
                            <div className="space-y-0.5">
                              {r.jodiNumber && <div><span className="text-gray-500">Jodi:</span> <span className="font-semibold text-gray-800">{r.jodiNumber}</span></div>}
                              {r.harufAndar && <div><span className="text-gray-500">Andar:</span> <span className="font-semibold text-gray-800">{r.harufAndar}</span></div>}
                              {r.harufBahar && <div><span className="text-gray-500">Bahar:</span> <span className="font-semibold text-gray-800">{r.harufBahar}</span></div>}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td> */}
                        <td className="px-4 py-2.5 border border-gray-200">
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              onClick={() => navigate(`/sports-betting/matka/${game.eventId}`)}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 text-xs font-medium transition-colors rounded-sm"
                            >
                              View
                            </button>
                            <button
                              onClick={() => navigate(`/matka/all-bets/${game.eventId}/${encodeURIComponent(game.name)}`)}
                              className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 text-xs font-medium transition-colors rounded-sm"
                            >
                              All Bets
                            </button>
                            {isAdmin && (
                              <button
                                onClick={() => setConfirmModal({ isOpen: true, game })}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 text-xs font-medium transition-colors rounded-sm"
                              >
                                Cancel Bet
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="flex justify-end items-center gap-1 px-4 py-3 border-t border-gray-200 bg-white">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center border border-gray-300 text-gray-500 disabled:opacity-40 hover:bg-gray-50 text-sm"
                >
                  ‹
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`w-8 h-8 flex items-center justify-center border text-sm font-medium ${
                      currentPage === p ? "border-blue-500 bg-blue-500 text-white" : "border-gray-300 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 flex items-center justify-center border border-gray-300 text-gray-500 disabled:opacity-40 hover:bg-gray-50 text-sm"
                >
                  ›
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>

    {confirmModal.isOpen && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center"
        style={{ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', backgroundColor: 'rgba(255,255,255,0.15)' }}
      >
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm mx-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Cancel Bet</h3>
          <p className="text-gray-600 text-sm mb-5">
            Kya aap <span className="font-bold text-gray-800 capitalize">{confirmModal.game?.name}</span> ki saari bets cancel karna chahte hain?
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setConfirmModal({ isOpen: false, game: null })}
              className="px-4 py-2 text-sm border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleCancelEvent}
              disabled={isCancelling}
              className="px-4 py-2 text-sm bg-red-500 hover:bg-red-600 text-white disabled:opacity-60"
            >
              {isCancelling ? 'Processing...' : 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default InplayMatkaPage;
